import { useState, useEffect, useMemo } from "react";
import { fetchFAQs, createFAQ, updateFAQ, deleteFAQ } from "../../../api/faqApi";
import { getAuditLogs } from "../../../api/auditApi";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useAuthUser } from "../../../contexts/AuthUserContext";
import { useCustomer } from "../../../contexts/CustomerContext";
import { useWeek } from "../../../contexts/WeekContext";

// Helper function to format timestamp
const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
        const date = new Date(timestamp);
        const localMonth = String(date.getMonth() + 1).padStart(2, '0');
        const localDay = String(date.getDate()).padStart(2, '0');
        const localYear = String(date.getFullYear()).slice(2);
        const localHour = String(date.getHours()).padStart(2, '0');
        const localMinute = String(date.getMinutes()).padStart(2, '0');
        return `${localMonth}/${localDay}/${localYear} - ${localHour}:${localMinute}`;
    } catch (error) {
        return 'Invalid Date';
    }
};

// Helper function to highlight search text
const highlightText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
        regex.test(part) ? (
            <span key={index} className="bg-yellow-200 text-yellow-900 px-1 rounded">
                {part}
            </span>
        ) : part
    );
};

// FAQs Table Component
const FAQsTable = ({ faqs, searchText, onEdit, onDelete }) => {
    return (
        <div className="w-full h-full rounded-md overflow-hidden flex flex-col">
            {/* Desktop Header (sm and above) */}
            <div className="w-full flex-shrink-0 hidden sm:block">
                <div className="flex text-font-primary py-3 px-0 card-container items-center text-sm font-semibold">
                    <div className="pl-4 flex-1">Question</div>
                    <div className="flex-1">Answer</div>
                    <div className="pl-2 w-[120px] max-w-[120px] flex justify-center gap-2">Actions</div>
                </div>
            </div>

            <div className="w-full flex-1 overflow-y-auto scrollable">
                {/* Desktop List View (sm and above) */}
                <div className="w-full hidden sm:block">
                    {faqs.map((faq, index) => {
                        const question = faq.question || 'No question';
                        const answer = faq.answer || 'No answer available';
                        const faqId = faq.id;
                        
                        return (
                            <div
                                key={index}
                                className="flex mb-2 text-font-secondary hover:text-font-primary hover:bg-background hover:button-container hover:border-none transition-colors ease-out-in duration-300 text-sm"
                            >
                                <div className="flex flex-col gap-1 pt-2 pl-4 flex-1 min-w-0 text-font-primary flex-shrink-0">
                                    <span className="font-semibold text-primary text-sm">{highlightText(question, searchText)}</span>
                                    <span className="text-sm text-font-secondary">{highlightText(formatTimestamp(faq.updated_at || faq.created_at), searchText)}</span>
                                </div>
                                <div className="p-2 pr-4 flex-1 min-w-0">
                                    <div className="text-sm leading-relaxed">
                                        {highlightText(answer, searchText)}
                                    </div>
                                </div>
                                <div className="flex items-center justify-center gap-2 w-[120px] max-w-[120px] flex-shrink-0">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEdit(faqId);
                                        }}
                                        className="p-2 text-primary hover:bg-accent hover:text-background rounded transition-colors duration-300"
                                        title="Edit FAQ"
                                    >
                                        <Icon icon="mdi:pencil" className="text-xl" />
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDelete(faqId);
                                        }}
                                        className="p-2 text-error hover:bg-error hover:text-background rounded transition-colors duration-300"
                                        title="Delete FAQ"
                                    >
                                        <Icon icon="mdi:delete" className="text-xl" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Mobile Card View (below sm) */}
                <div className="w-full sm:hidden p-2">
                    {faqs.map((faq, index) => {
                        const question = faq.question || 'No question';
                        const answer = faq.answer || 'No answer available';
                        const faqId = faq.id;
                        
                        return (
                            <div
                                key={index}
                                className="p-3 rounded-md transition-all duration-300 ease-out-in hover:shadow-button group hover:scale-[1.01] card-container bg-background border border-bgAccent min-h-[60px] flex flex-col mb-2"
                            >
                                <div className="flex items-start justify-between mb-2 flex-shrink-0">
                                    <div className="flex flex-col flex-1">
                                        <span className="font-semibold text-primary text-base">
                                            {highlightText(question, searchText)}
                                        </span>
                                        <span className="text-xs text-font-secondary">
                                            {highlightText(formatTimestamp(faq.updated_at || faq.created_at), searchText)}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 ml-2">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onEdit(faqId);
                                            }}
                                            className="p-2 text-primary hover:bg-accent hover:text-background rounded transition-colors duration-300"
                                            title="Edit FAQ"
                                        >
                                            <Icon icon="mdi:pencil" className="text-xl" />
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDelete(faqId);
                                            }}
                                            className="p-2 text-error hover:bg-error hover:text-background rounded transition-colors duration-300"
                                            title="Delete FAQ"
                                        >
                                            <Icon icon="mdi:delete" className="text-xl" />
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-2">
                                    <p className="text-sm text-font-secondary leading-relaxed group-hover:text-font-primary">
                                        {highlightText(answer, searchText)}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {faqs.length === 0 && (
                    <div className="text-center py-10 text-gray-400">No FAQs found.</div>
                )}
            </div>
        </div>
    );
};

// History Table Component
const HistoryTable = ({ historyLogs, searchText }) => {
    const formatActionType = (actionType) => {
        if (!actionType) return 'Unknown';
        return actionType.replace('FAQ_', '').replace('_', ' ').toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const getActionIcon = (actionType) => {
        if (actionType?.includes('CREATED')) return 'material-symbols:add-circle-outline';
        if (actionType?.includes('UPDATED')) return 'material-symbols:edit-outline';
        if (actionType?.includes('DELETED')) return 'material-symbols:delete-outline';
        return 'material-symbols:info-outline';
    };

    const getActionColor = (actionType) => {
        if (actionType?.includes('CREATED')) return 'text-green-500';
        if (actionType?.includes('UPDATED')) return 'text-blue-500';
        if (actionType?.includes('DELETED')) return 'text-error';
        return 'text-font-secondary';
    };

    return (
        <div className="w-full h-full rounded-md overflow-hidden flex flex-col">
            {/* Desktop Header (sm and above) */}
            <div className="w-full flex-shrink-0 hidden sm:block">
                <div className="flex text-font-primary py-3 px-0 card-container items-center text-sm font-semibold">
                    <div className="pl-4 w-[150px] max-w-[150px]">Action</div>
                    <div className="pl-2 flex-1">Details</div>
                    <div className="pl-2 w-[180px] max-w-[180px]">Timestamp</div>
                </div>
            </div>

            <div className="w-full flex-1 overflow-y-auto scrollable">
                {/* Desktop List View (sm and above) */}
                <div className="w-full hidden sm:block">
                    {historyLogs.map((log, index) => {
                        const actionType = log.action_type || '';
                        const details = log.details || {};
                        const user = log.admin_user_name || log.admin_user_email || 'Unknown';
                        const timestamp = log.timestamp || log.created_at;
                        
                        // Format details for display
                        let detailsText = '';
                        if (actionType === 'FAQ_CREATED') {
                            detailsText = `Created: "${details.question || 'N/A'}"`;
                        } else if (actionType === 'FAQ_UPDATED') {
                            const changes = details.changes || {};
                            const changeParts = [];
                            // Handle question changes: changes.question.old and changes.question.new
                            if (changes.question) {
                                const questionChange = changes.question;
                                changeParts.push(`Question: "${questionChange.old || 'N/A'}" → "${questionChange.new || 'N/A'}"`);
                            }
                            // Handle answer changes: changes.answer.old and changes.answer.new
                            if (changes.answer) {
                                const answerChange = changes.answer;
                                changeParts.push(`Answer: "${answerChange.old || 'N/A'}" → "${answerChange.new || 'N/A'}"`);
                            }
                            detailsText = changeParts.length > 0 ? changeParts.join(' | ') : 'Updated FAQ';
                        } else if (actionType === 'FAQ_DELETED') {
                            // Use deleted_question and deleted_answer fields
                            const deletedQuestion = details.deleted_question || 'N/A';
                            const deletedAnswer = details.deleted_answer || 'N/A';
                            detailsText = `Deleted: "${deletedQuestion}" (Answer: "${deletedAnswer}")`;
                        } else {
                            detailsText = JSON.stringify(details);
                        }
                        
                        return (
                            <div
                                key={index}
                                className="flex mb-2 text-font-secondary hover:text-font-primary hover:bg-background hover:button-container hover:border-none transition-colors ease-out-in duration-300 text-sm"
                            >
                                <div className="flex items-center pl-4 w-[150px] max-w-[150px] flex-shrink-0">
                                    <div className={`flex items-center gap-1 ${getActionColor(actionType)}`}>
                                        <Icon icon={getActionIcon(actionType)} className="text-lg" />
                                        <span className="font-semibold text-sm">{formatActionType(actionType)}</span>
                                    </div>
                                </div>
                                <div className="p-2 pr-4 flex-1 min-w-0">
                                    <div className="text-sm leading-relaxed">
                                        {highlightText(detailsText, searchText)}
                                    </div>
                                </div>
                                <div className="p-2 pr-4 w-[180px] max-w-[180px] flex-shrink-0 text-sm text-font-secondary">
                                    {highlightText(formatTimestamp(timestamp), searchText)}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Mobile Card View (below sm) */}
                <div className="w-full sm:hidden p-2">
                    {historyLogs.map((log, index) => {
                        const actionType = log.action_type || '';
                        const details = log.details || {};
                        const user = log.admin_user_name || log.admin_user_email || 'Unknown';
                        const timestamp = log.timestamp || log.created_at;
                        
                        let detailsText = '';
                        if (actionType === 'FAQ_CREATED') {
                            detailsText = `Created: "${details.question || 'N/A'}"`;
                        } else if (actionType === 'FAQ_UPDATED') {
                            const changes = details.changes || {};
                            const changeParts = [];
                            // Handle question changes: changes.question.old and changes.question.new
                            if (changes.question) {
                                const questionChange = changes.question;
                                changeParts.push(`Question: "${questionChange.old || 'N/A'}" → "${questionChange.new || 'N/A'}"`);
                            }
                            // Handle answer changes: changes.answer.old and changes.answer.new
                            if (changes.answer) {
                                const answerChange = changes.answer;
                                changeParts.push(`Answer: "${answerChange.old || 'N/A'}" → "${answerChange.new || 'N/A'}"`);
                            }
                            detailsText = changeParts.length > 0 ? changeParts.join(' | ') : 'Updated FAQ';
                        } else if (actionType === 'FAQ_DELETED') {
                            // Use deleted_question and deleted_answer fields
                            const deletedQuestion = details.deleted_question || 'N/A';
                            const deletedAnswer = details.deleted_answer || 'N/A';
                            detailsText = `Deleted: "${deletedQuestion}" (Answer: "${deletedAnswer}")`;
                        } else {
                            detailsText = JSON.stringify(details);
                        }
                        
                        return (
                            <div
                                key={index}
                                className="p-3 rounded-md transition-all duration-300 ease-out-in hover:shadow-button group hover:scale-[1.01] card-container bg-background border border-bgAccent min-h-[60px] flex flex-col mb-2"
                            >
                                <div className="flex items-start justify-between mb-2 flex-shrink-0">
                                    <div className="flex items-center gap-2">
                                        <Icon icon={getActionIcon(actionType)} className={`text-xl ${getActionColor(actionType)}`} />
                                        <div className="flex flex-col">
                                            <span className={`font-semibold text-sm ${getActionColor(actionType)}`}>
                                                {formatActionType(actionType)}
                                            </span>
                                        </div>
                                    </div>
                                    <span className="text-xs text-font-secondary">
                                        {highlightText(formatTimestamp(timestamp), searchText)}
                                    </span>
                                </div>

                                <div className="mt-2">
                                    <p className="text-sm text-font-secondary leading-relaxed group-hover:text-font-primary">
                                        {highlightText(detailsText, searchText)}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {historyLogs.length === 0 && (
                    <div className="text-center py-10 text-gray-400">No history found.</div>
                )}
            </div>
        </div>
    );
};

// Edit/Create FAQ Modal Component
const FAQModal = ({ isOpen, onClose, onSave, initialData = null }) => {
    const [question, setQuestion] = useState('');
    const [answer, setAnswer] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                setQuestion(initialData.question || '');
                setAnswer(initialData.answer || '');
            } else {
                setQuestion('');
                setAnswer('');
            }
        }
    }, [isOpen, initialData]);

    const handleSave = async () => {
        if (!question.trim() || !answer.trim()) {
            alert('Please fill in both question and answer');
            return;
        }

        setIsSaving(true);
        try {
            await onSave({ question: question.trim(), answer: answer.trim() });
            onClose();
        } catch (error) {
            console.error('Error saving FAQ:', error);
            alert('Failed to save FAQ. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-background rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-font-primary">
                        {initialData ? 'Edit FAQ' : 'Create New FAQ'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-font-secondary hover:text-font-primary transition-colors"
                    >
                        <Icon icon="material-symbols:close" className="text-2xl" />
                    </button>
                </div>

                <div className="flex flex-col gap-4">
                    <div>
                        <label className="block text-sm font-medium text-font-primary mb-2">
                            Question
                        </label>
                        <textarea
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            className="w-full p-3 button-container rounded-md text-sm text-font-primary resize-none"
                            rows={3}
                            placeholder="Enter the question..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-font-primary mb-2">
                            Answer
                        </label>
                        <textarea
                            value={answer}
                            onChange={(e) => setAnswer(e.target.value)}
                            className="w-full p-3 button-container rounded-md text-sm text-font-primary resize-none"
                            rows={6}
                            placeholder="Enter the answer..."
                        />
                    </div>

                    <div className="flex justify-end gap-2 mt-4">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 button-container rounded-md text-sm text-font-secondary hover:text-font-primary transition-colors"
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            className="px-4 py-2 bg-primary text-background rounded-md text-sm hover:bg-primary-70 transition-colors"
                            disabled={isSaving}
                        >
                            {isSaving ? 'Saving...' : (initialData ? 'Update' : 'Create')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const FAQs = () => {
    const [faqs, setFaqs] = useState([]);
    const [historyLogs, setHistoryLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [activeTab, setActiveTab] = useState('faqs');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingFAQ, setEditingFAQ] = useState(null);
    const { getEffectiveCustomerId } = useCustomer();
    const currentUser = useAuthUser();
    const { dateRange } = useWeek();

    const customerId = getEffectiveCustomerId();

    // Animation states
    const [animationStates, setAnimationStates] = useState({
        header: false,
        content: false
    });

    // Trigger staggered animations on component mount
    useEffect(() => {
        const timeouts = [];

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, header: true }));
        }, 100));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, content: true }));
        }, 300));

        return () => {
            timeouts.forEach(timeout => clearTimeout(timeout));
        };
    }, []);

    // Load FAQs
    const loadFAQs = async () => {
        try {
            setLoading(true);
            const response = await fetchFAQs(customerId);
            
            if (response?.error) {
                setError(response.message || 'Failed to load FAQs');
                setFaqs([]);
                return;
            }
            
            // Handle different response structures
            let faqsData = [];
            if (Array.isArray(response)) {
                faqsData = response;
            } else if (response?.faqs && Array.isArray(response.faqs)) {
                faqsData = response.faqs;
            } else if (response?.data) {
                if (Array.isArray(response.data)) {
                    faqsData = response.data;
                } else if (response.data.faqs && Array.isArray(response.data.faqs)) {
                    faqsData = response.data.faqs;
                }
            }
            
            setFaqs(faqsData);
            setError(null);
        } catch (err) {
            setError('Failed to load FAQs');
            console.error('Error loading FAQs:', err);
            setFaqs([]);
        } finally {
            setLoading(false);
        }
    };

    // Load history
    const loadHistory = async () => {
        try {
            setHistoryLoading(true);
            
            // Helper function to extract logs from different response structures
            const extractLogs = (response) => {
                if (!response) return [];
                
                // Direct array
                if (Array.isArray(response)) {
                    return response;
                }
                
                // Object with logs property
                if (response.logs && Array.isArray(response.logs)) {
                    return response.logs;
                }
                
                // Nested data structure
                if (response.data) {
                    if (Array.isArray(response.data)) {
                        return response.data;
                    }
                    if (response.data.logs && Array.isArray(response.data.logs)) {
                        return response.data.logs;
                    }
                }
                
                return [];
            };
            
            // Prepare date range - must use WeekContext (API requires it)
            if (!dateRange?.start || !dateRange?.end) {
                console.warn('No date range set, cannot fetch audit logs');
                setHistoryLogs([]);
                setHistoryLoading(false);
                return;
            }
            
            // Use date range from WeekContext
            const start = new Date(dateRange.start);
            start.setHours(0, 0, 0, 0);
            const startDate = start.toISOString();
            
            const end = new Date(dateRange.end);
            end.setHours(23, 59, 59, 999);
            const endDate = end.toISOString();
            
            // Fetch all FAQ-related audit logs with date range
            let allLogs = [];
            
            try {
                console.log('Fetching FAQ audit logs with date range:', { startDate, endDate });
                
                // Build filter params exactly like AuditLogTab does
                // Note: API has max pageSize of 200
                const filterParams = {
                    startDate,
                    endDate,
                    page: 1, 
                    pageSize: 200
                };
                
                // Fetch all logs with date range and filter client-side for FAQ actions
                // This avoids the 422 error from action_type filter
                const response = await getAuditLogs(filterParams);
                
                console.log('Raw audit logs response:', response);
                
                const logs = extractLogs(response);
                console.log('Extracted logs:', logs.length, 'total logs');
                
                // Filter for FAQ-related actions
                allLogs = logs.filter(log => {
                    const actionType = log.action_type || log.actionType || '';
                    const isFAQAction = actionType === 'FAQ_CREATED' || 
                                       actionType === 'FAQ_UPDATED' || 
                                       actionType === 'FAQ_DELETED';
                    if (isFAQAction) {
                        console.log('Found FAQ log:', log);
                    }
                    return isFAQAction;
                });
                
                console.log('Filtered FAQ logs:', allLogs.length);
            } catch (err) {
                console.error('Error in primary history fetch:', err);
                console.error('Error response data:', err.response?.data);
                console.error('Error response status:', err.response?.status);
                if (err.response?.data?.detail) {
                    console.error('Error detail:', JSON.stringify(err.response.data.detail, null, 2));
                }
                
                // Fallback to individual calls
                try {
                    const filterParams = {
                        page: 1, 
                        pageSize: 200  // API max is 200
                    };
                    if (startDate) filterParams.startDate = startDate;
                    if (endDate) filterParams.endDate = endDate;
                    
                    const [createdResponse, updatedResponse, deletedResponse] = await Promise.all([
                        getAuditLogs({ 
                            ...filterParams,
                            action_type: 'FAQ_CREATED'
                        }).catch((e) => {
                            console.error('FAQ_CREATED error:', e.response?.data);
                            if (e.response?.data?.detail) {
                                console.error('FAQ_CREATED detail:', JSON.stringify(e.response.data.detail, null, 2));
                            }
                            return null;
                        }),
                        getAuditLogs({ 
                            ...filterParams,
                            action_type: 'FAQ_UPDATED'
                        }).catch((e) => {
                            console.error('FAQ_UPDATED error:', e.response?.data);
                            if (e.response?.data?.detail) {
                                console.error('FAQ_UPDATED detail:', JSON.stringify(e.response.data.detail, null, 2));
                            }
                            return null;
                        }),
                        getAuditLogs({ 
                            ...filterParams,
                            action_type: 'FAQ_DELETED'
                        }).catch((e) => {
                            console.error('FAQ_DELETED error:', e.response?.data);
                            if (e.response?.data?.detail) {
                                console.error('FAQ_DELETED detail:', JSON.stringify(e.response.data.detail, null, 2));
                            }
                            return null;
                        })
                    ]);

                    allLogs = [
                        ...extractLogs(createdResponse),
                        ...extractLogs(updatedResponse),
                        ...extractLogs(deletedResponse)
                    ];
                } catch (fallbackErr) {
                    console.error('Fallback history loading failed:', fallbackErr);
                }
            }

            // Sort by timestamp descending (newest first)
            allLogs.sort((a, b) => {
                const timeA = new Date(a.timestamp || a.created_at || 0);
                const timeB = new Date(b.timestamp || b.created_at || 0);
                return timeB - timeA;
            });

            console.log('Final FAQ history logs:', allLogs.length, allLogs);
            setHistoryLogs(allLogs);
        } catch (err) {
            console.error('Error loading history:', err);
            setHistoryLogs([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    useEffect(() => {
        if (customerId === 'kronk') {
            loadFAQs();
        }
    }, [customerId]);

    useEffect(() => {
        if (activeTab === 'history' && customerId === 'kronk') {
            // Only load history if we have a date range (like AuditLogTab does)
            if (dateRange?.start && dateRange?.end) {
                loadHistory();
            } else {
                // If no date range, set empty logs and show message
                setHistoryLogs([]);
                setHistoryLoading(false);
            }
        }
    }, [activeTab, customerId, dateRange?.start, dateRange?.end]);

    const handleEditFAQ = (faqId) => {
        const faq = faqs.find(f => f.id === faqId);
        setEditingFAQ(faq);
        setIsModalOpen(true);
    };

    const handleAddNewFAQ = () => {
        setEditingFAQ(null);
        setIsModalOpen(true);
    };

    const handleSaveFAQ = async (faqData) => {
        try {
            if (editingFAQ) {
                await updateFAQ(customerId, editingFAQ.id, faqData);
            } else {
                await createFAQ(customerId, faqData);
            }
            await loadFAQs();
            if (activeTab === 'history') {
                await loadHistory();
            }
        } catch (error) {
            console.error('Error saving FAQ:', error);
            throw error;
        }
    };

    const handleDeleteFAQ = async (faqId) => {
        if (!window.confirm('Are you sure you want to delete this FAQ?')) {
            return;
        }

        try {
            await deleteFAQ(customerId, faqId);
            await loadFAQs();
            if (activeTab === 'history') {
                await loadHistory();
            }
        } catch (error) {
            console.error('Error deleting FAQ:', error);
            alert('Failed to delete FAQ. Please try again.');
        }
    };

    // Filter FAQs based on search text
    const filteredFAQs = useMemo(() => {
        if (searchText === '') return faqs;
        
        const searchLower = searchText.toLowerCase();
        return faqs.filter(faq => {
            const question = (faq.question || '').toLowerCase();
            const answer = (faq.answer || '').toLowerCase();
            return question.includes(searchLower) || answer.includes(searchLower);
        });
    }, [faqs, searchText]);

    // Filter history based on search text
    const filteredHistory = useMemo(() => {
        if (searchText === '') return historyLogs;
        
        const searchLower = searchText.toLowerCase();
        return historyLogs.filter(log => {
            const actionType = (log.action_type || '').toLowerCase();
            const user = ((log.admin_user_name || log.admin_user_email || '') + '').toLowerCase();
            const details = JSON.stringify(log.details || {}).toLowerCase();
            const timestamp = formatTimestamp(log.timestamp || log.created_at).toLowerCase();
            return actionType.includes(searchLower) || 
                   user.includes(searchLower) || 
                   details.includes(searchLower) ||
                   timestamp.includes(searchLower);
        });
    }, [historyLogs, searchText]);

    // Check if customer is Kronk
    if (customerId !== 'kronk') {
        return (
            <div className="responsive-page-layout">
                <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                        <Icon icon="material-symbols:block" className="text-4xl text-bgAccent mb-4" />
                        <p className="text-font-secondary">FAQs are only available for Kronk customer</p>
                    </div>
                </div>
            </div>
        );
    }

    if (loading && activeTab === 'faqs') {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-font-secondary">Loading FAQs...</div>
            </div>
        );
    }

    if (error && activeTab === 'faqs') {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-error">{error}</div>
            </div>
        );
    }

    return (
        <div className="responsive-page-layout">
            <div className={`flex flex-col lg:flex-row justify-between lg:items-center items-start w-full gap-4 lg:gap-0 transition-all duration-700 ease-out ${
                animationStates.header
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
            }`}>
                <div className="flex flex-col gap-2 items-start">
                    <h1 className="header-textbox">
                        <Icon icon="material-symbols:help-outline" className="header-icon"></Icon>
                        Knowledge Base
                    </h1>
                    <p className="text-font-secondary text-sm sm:text-base">
                        {activeTab === 'faqs' 
                            ? `View and manage FAQs (${filteredFAQs.length} of ${faqs.length} FAQs)`
                            : `View change history (${filteredHistory.length} entries)`
                        }
                    </p>
                </div>
                {activeTab === 'faqs' && (
                    <div className="flex flex-row gap-2 w-full lg:w-auto">
                        <div className="relative w-full sm:w-auto">
                            <input
                                type="text"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                placeholder="Search FAQs..."
                                className="p-2 sm:p-3 button-container rounded-md text-xs sm:text-sm w-full sm:w-auto pl-3"
                            />
                        </div>
                        <button
                            onClick={handleAddNewFAQ}
                            className="flex flex-row gap-2 items-center text-font-primary button-container px-3 py-2 rounded-lg text-sm group hover:bg-primary transition-colors duration-300"
                        >
                            <Icon icon="proicons:box-add" className="text-xl text-primary group-hover:text-background" />
                            <span className="text-sm font-medium group-hover:text-background">Add New FAQ</span>
                        </button>
                    </div>
                )}
                {activeTab === 'history' && (
                    <div className="relative w-full sm:w-auto">
                        <input
                            type="text"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            placeholder="Search history..."
                            className="p-2 sm:p-3 button-container rounded-md text-xs sm:text-sm w-full sm:w-auto pl-3"
                        />
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div className="flex-shrink-0 w-full flex flex-row items-center text-sm sm:text-base border-b border-border">
                <button
                    onClick={() => setActiveTab('faqs')}
                    className={`flex-1 flex flex-row justify-center items-center gap-1 p-2 min-w-0 whitespace-nowrap ${
                        activeTab === 'faqs' ? "tickets-tab-active" : "tickets-tab-inactive"
                    }`}
                >
                    <Icon icon="material-symbols:help-outline" className={`text-lg sm:text-xl ${
                        activeTab === 'faqs' ? "text-primary" : ""
                    }`} />
                    <span className={`hidden sm:inline ${activeTab === 'faqs' ? 'font-semibold' : 'font-medium'}`}>
                        FAQs
                    </span>
                </button>
                <button
                    onClick={() => setActiveTab('history')}
                    className={`flex-1 flex flex-row justify-center items-center gap-1 p-2 min-w-0 whitespace-nowrap ${
                        activeTab === 'history' ? "tickets-tab-active" : "tickets-tab-inactive"
                    }`}
                >
                    <Icon icon="material-symbols:history" className={`text-lg sm:text-xl ${
                        activeTab === 'history' ? "text-primary" : ""
                    }`} />
                    <span className={`hidden sm:inline ${activeTab === 'history' ? 'font-semibold' : 'font-medium'}`}>
                        History
                    </span>
                </button>
            </div>
            
            <div className={`w-full flex-1 min-h-0 transition-all duration-700 ease-out ${
                animationStates.content
                    ? 'opacity-100 translate-y-0 scale-100'
                    : 'opacity-0 translate-y-4 scale-95'
            }`}>
                {activeTab === 'faqs' ? (
                    <FAQsTable 
                        faqs={filteredFAQs}
                        searchText={searchText}
                        onEdit={handleEditFAQ}
                        onDelete={handleDeleteFAQ}
                    />
                ) : (
                    historyLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-font-secondary">Loading history...</div>
                        </div>
                    ) : (
                        <HistoryTable 
                            historyLogs={filteredHistory}
                            searchText={searchText}
                        />
                    )
                )}
            </div>

            {/* Modal */}
            <FAQModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingFAQ(null);
                }}
                onSave={handleSaveFAQ}
                initialData={editingFAQ}
            />
        </div>
    );
};

export default FAQs;
